import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Plane, Radio, Search, CheckCircle2, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface ATCFeed {
  id: string;
  airport_code: string;
  airport_name: string;
  city: string;
  country: string;
  stream_url: string;
  frequency: string;
  feed_type: string;
  is_active: boolean;
}

interface ATCSelectorProps {
  selectedFeedUrl: string | null;
  onSelectFeed: (feed: ATCFeed) => void;
}

export const ATCSelector = ({ selectedFeedUrl, onSelectFeed }: ATCSelectorProps) => {
  const [feeds, setFeeds] = useState<ATCFeed[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    fetchFeeds();
  }, []);

  const fetchFeeds = async () => {
    try {
      const { data, error } = await supabase
        .from('atc_feeds')
        .select('*')
        .order('country', { ascending: true })
        .order('city', { ascending: true })
        .order('is_active', { ascending: false });

      if (error) throw error;
      setFeeds(data || []);
    } catch (error: any) {
      console.error('Error fetching ATC feeds:', error);
      toast({
        title: "Error loading feeds",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-pulse text-muted-foreground">Loading airports...</div>
      </div>
    );
  }

  // Filter feeds based on search query
  const filteredFeeds = feeds.filter(feed => 
    feed.airport_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    feed.airport_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    feed.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
    feed.country.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedFeeds = filteredFeeds.reduce((acc, feed) => {
    if (!acc[feed.country]) {
      acc[feed.country] = [];
    }
    acc[feed.country].push(feed);
    return acc;
  }, {} as Record<string, ATCFeed[]>);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Search airports, cities, or codes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>
      
      {filteredFeeds.length === 0 ? (
        <div className="text-center p-8 text-muted-foreground">
          No airports found matching "{searchQuery}"
        </div>
      ) : (
        <div className="space-y-6 max-h-[500px] overflow-y-auto pr-2">
      {Object.entries(groupedFeeds).map(([country, countryFeeds]) => (
        <div key={country}>
          <h4 className="text-sm font-semibold mb-3 text-muted-foreground flex items-center gap-2">
            <Plane className="h-4 w-4" />
            {country}
          </h4>
          <div className="space-y-2">
            {countryFeeds.map((feed) => (
              <Card
                key={feed.id}
                className={`p-3 cursor-pointer transition-all ${
                  !feed.is_active 
                    ? 'opacity-60' 
                    : ''
                } ${
                  selectedFeedUrl === feed.stream_url
                    ? 'ring-2 ring-primary bg-primary/5'
                    : 'hover:bg-accent/50 hover:shadow-lg'
                }`}
                onClick={() => onSelectFeed(feed)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {feed.airport_code}
                      </Badge>
                      {feed.is_active ? (
                        <div className="flex items-center gap-1 text-green-500">
                          <CheckCircle2 className="h-3 w-3" />
                          <span className="text-xs font-medium">Online</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-red-500">
                          <XCircle className="h-3 w-3" />
                          <span className="text-xs font-medium">Offline</span>
                        </div>
                      )}
                      {selectedFeedUrl === feed.stream_url && feed.is_active && (
                        <div className="flex items-center gap-1 text-primary">
                          <Radio className="h-3 w-3 animate-pulse" />
                          <span className="text-xs font-medium">LIVE</span>
                        </div>
                      )}
                    </div>
                    <h5 className="font-semibold text-sm truncate">{feed.airport_name}</h5>
                    <p className="text-xs text-muted-foreground">{feed.city}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <Badge className="text-xs">{feed.feed_type}</Badge>
                    {feed.frequency && (
                      <p className="text-xs text-muted-foreground mt-1">{feed.frequency} MHz</p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      ))}
        </div>
      )}
    </div>
  );
};
